import _ from 'lodash';
import { required } from 'vuelidate/lib/validators';
import ToolKitMixin from '@/app/mixins/toolkit/item';
import {
  getAgeAtSpecificDate,
  getPreservationAge,
  getFormattedMemberName,
} from '@/app/utils/utils';
import { defaultPensionKitEstablishment } from '@/app/utils/toolkit/defaults/defaultPensionKitEstablishment';

export default {
  name: 'pension-establishment-member-selection',
  mixins: [ToolKitMixin],
  data: () => ({
    chosenMember: null,
  }),
  methods: {
    handleNextStep() {
      this.$emit('nextStep');
    },
    checkChosenMember() {
      const foundMember = this.$store.getters['fund/getFund'].members.filter(
        fundMember => this.getItem.requirements.member.uuid === fundMember.uuid,
      );
      if (foundMember.length === 1) {
        const updatedMember = {
          ...this.getItem.requirements.member,
          dateOfBirth: foundMember[0].dateOfBirth,
          value() {
            return getFormattedMemberName(foundMember[0]);
          },
        };
        const data = this.getItem;
        this.updateMember(data, updatedMember);
      }
      return '';
    },
    updateMember(data, member) {
      const { startDate } = this;
      let specificDate = new Date();
      if (startDate !== '') {
        specificDate = new Date(startDate);
      }
      data.requirements.startDate = startDate;
      const newMember = {
        uuid: member.uuid,
        preservationAge: getPreservationAge(member.dateOfBirth),
        age: getAgeAtSpecificDate(member.dateOfBirth, specificDate),
        dateOfBirth: member.dateOfBirth,
        name: member.value(),
      };
      data.requirements.member = newMember;
      this.chosenMember = newMember;
      this.setItem({ requirements: { member: newMember } });
    },
    updateChosenMember(member) {
      const data = _.cloneDeep(defaultPensionKitEstablishment);
      this.updateMember(data, member);
    },
  },
  computed: {
    members() {
      const { members } = this.$store.getters['fund/getFund'];
      return members.map(member => ({
        ...member,
        value() {
          return getFormattedMemberName(member);
        },
      }));
    },
    getRatifyText() {
      if (this.getItem.requirements.ratify === false) {
        return [
          'Legally, a pension commences when the member requests a pension and the trustee agrees to provide one.  The pension can commence from a specific date in the future but only if this process has occurred first. The trust deed might impose extra requirements such as written requests or acknowledgments.',
          "It is therefore common to prepare the key documents (a request from the member to start a pension and the trustee's agreement to do so) even if exact amounts such as the size of the pension and its tax components are not yet known.  These amounts can follow later in a separate schedule (which our tool will produce).  Selecting this option means we will prepare the key documents to be signed before the pension commencement date and a separate schedule showing exact amounts which can be completed at the same time or some later date when all the necessary information is known.",
          "Use this option, for example, if you wish to record the commencement of a pension in the future with all of a member's accumulation balance.  The tool will allow you to document this decision, the terms of the pension and provide a Product Disclosure Statement for signing immediately and return at a later date (once the exact balance is known) to prepare the relevant schedule.",
        ];
      }
      if (this.getItem.requirements.ratify === true) {
        return [
          'Legally, a pension commences when the member requests a pension and the trustee agrees to provide one.  The pension can commence from a specific date in the future but only if this process has occurred first.  Often, however, members and trustees agree verbally that a pension will commence at a particular time and only confirm or ratify this in writing at a later date.  In other words, the decision is made in advance but the documentation is prepared retrospectively.',
          "Use this option, for example, if the trustee and members agreed to commence a pension from 1 July but did not formally document it at the time.  The documents will clarify the verbal agreement, formally record the trustee's acceptance of the member's request, set out the terms of the pension and provide a Product Disclosure Statement now even though the pension started from 1 July. They will be specifically worded to identify that they are confirming a decision that has already been made.",
        ];
      }
      return [];
    },
    showPdsMessage() {
      const startDate = new Date(this.startDate);
      if (startDate < new Date('July 1, 2018')) {
        return 'A product disclosure statement (PDS) will be produced as part of this document pack.  The earliest PDS available is 2018/19 and this will be provided in this case.';
      }
      if (startDate >= new Date(2025, 6, 1)) {
        return 'A product disclosure statement (PDS) will be produced as part of this document pack. Given the date of the commencement of this pension, some tax thresholds may change between now and when the pension commences.';
      }
      return null;
    },
    ratify: {
      get() {
        if (this.getItem.requirements.ratify === null) {
          return null;
        }
        if (this.getItem.requirements.ratify) {
          return 'ratify decisions and agreements already made between the trustee and the member';
        }
        return 'document a decision being made now to commence the pension';
      },
      set(value) {
        if (
          value === 'ratify decisions and agreements already made between the trustee and the member'
        ) {
          this.setItem({ requirements: { ratify: true } });
        } else {
          this.setItem({ requirements: { ratify: false } });
        }
      },
    },
    startDate: {
      get() {
        if (this.getItem.requirements.startDate !== '') {
          this.$v.$touch();
        }
        return this.getItem.requirements.startDate;
      },
      set(value) {
        const storedDate = new Date(this.getItem.requirements.startDate);
        // eslint-disable-next-line max-len
        if (value instanceof Date && value.toLocaleDateString() !== storedDate.toLocaleDateString()) {
          this.setItem({ requirements: { startDate: value.toISOString() } });
        }
      },
    },
  },
  activated() {
    this.checkChosenMember();
  },
  validations: {
    startDate: {
      required,
      beforeValidDate: value => new Date(value) >= new Date('July 1, 2007'),
    },
    chosenMember: {
      name: {
        required,
      },
    },
    ratify: {
      required,
    },
  },
};
